import { useState, useMemo, useRef, useCallback } from 'react';
import { Leaf, Plus, Search } from 'lucide-react';
import { useDashboard } from '../../context/DashboardContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

import { getPlantImageUrl } from '../../utils/plantImageUtils';
import PlantDetailSheet from './PlantDetailSheet';

/**
 * PlantListView — "Garten" mobile tab (Screen 09).
 *
 * Shows placed plants (deduped by ID, sorted alphabetically).
 * Tapping a plant opens PlantDetailSheet.
 *
 * States:
 *  - Loading: placed plants exist but allPlants catalog not yet fetched
 *  - Empty: no placed plants → CTA to open editor
 *  - Default (≤ 49 plants): alphabetical list
 *  - Many (50+ plants): grouped by care phase with sticky headers
 */
const PlantListView = () => {
  const { placedPlants, allPlants, carePhase, setMobileTab, setActiveView } = useDashboard();
  const { isMobile } = useBreakpoint();

  // Mobile uses mobileTab routing; desktop uses activeView
  const openEditor = useCallback(() => {
    if (isMobile) setMobileTab('editor');
    else setActiveView('editor');
  }, [isMobile, setMobileTab, setActiveView]);

  const [search, setSearch] = useState('');
  const [selectedPlant, setSelectedPlant] = useState(null);
  const isOnline = useOnlineStatus();
  const [refreshing, setRefreshing] = useState(false);
  const [pullDelta, setPullDelta] = useState(0);
  const touchStartYRef = useRef(null);

  // Resolve placed plant IDs → full plant objects (deduped)
  const resolvedPlants = useMemo(() => {
    const seenIds = new Map();
    placedPlants.forEach(p => {
      const id = typeof p === 'object' && p !== null ? (p.id ?? p.plantId ?? null) : p;
      if (id !== null && !seenIds.has(id)) seenIds.set(id, null);
    });

    allPlants.forEach(plant => {
      if (seenIds.has(plant.id)) seenIds.set(plant.id, plant);
    });

    return [...seenIds.values()]
      .filter(Boolean)
      .sort((a, b) => {
        const nameA = (a.german_name || a.name || '').toLowerCase();
        const nameB = (b.german_name || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB, 'de');
      });
  }, [placedPlants, allPlants]);

  const filteredPlants = useMemo(() => {
    if (!search.trim()) return resolvedPlants;
    const q = search.toLowerCase();
    return resolvedPlants.filter(p => {
      const name = (p.german_name || p.name || '').toLowerCase();
      const latin = (p.scientific_name || '').toLowerCase();
      return name.includes(q) || latin.includes(q);
    });
  }, [resolvedPlants, search]);

  const activeIds = useMemo(
    () => new Set((carePhase?.activePlants || []).map(p => p.id)),
    [carePhase]
  );

  // Pull-to-refresh — matches PflegekalenderView pattern
  const onTouchStart = useCallback((e) => {
    touchStartYRef.current = e.touches[0].clientY;
  }, []);

  const onTouchMove = useCallback((e) => {
    if (touchStartYRef.current === null) return;
    const delta = e.touches[0].clientY - touchStartYRef.current;
    if (delta > 0) setPullDelta(Math.min(70, delta));
  }, []);

  const onTouchEnd = useCallback(() => {
    if (pullDelta > 60 && !refreshing) {
      setRefreshing(true);
      setTimeout(() => setRefreshing(false), 1000);
    }
    setPullDelta(0);
    touchStartYRef.current = null;
  }, [pullDelta, refreshing]);

  const isEmpty = resolvedPlants.length === 0;
  // Placed plants exist but catalog hasn't loaded from backend yet
  const isLoading = placedPlants.length > 0 && allPlants.length === 0;
  const useGroupedLayout = resolvedPlants.length >= 50;

  // Group plants for 50+ state
  const { activePlants: activeGroup, otherPlants: otherGroup } = useMemo(() => {
    if (!useGroupedLayout) return { activePlants: [], otherPlants: [] };
    return {
      activePlants: filteredPlants.filter(p => activeIds.has(p.id)),
      otherPlants: filteredPlants.filter(p => !activeIds.has(p.id)),
    };
  }, [filteredPlants, activeIds, useGroupedLayout]);

  const renderPlantRow = (plant) => {
    const displayName = plant.german_name || plant.name;
    const isActive = activeIds.has(plant.id);
    const imageName = plant.name || plant.german_name;

    return (
      <li key={plant.id}>
        <button
          className="w-full flex items-center gap-3 px-4 py-3 min-h-[44px] hover:bg-gray-50 border-b border-gray-50 transition-colors text-left"
          onClick={() => setSelectedPlant(plant)}
        >
          {/* Plant icon */}
          <div className="w-10 h-10 flex-shrink-0 bg-emerald-50 border border-emerald-200 rounded-lg overflow-hidden flex items-center justify-center">
            {imageName ? (
              <img
                src={getPlantImageUrl(imageName, 'thumb')}
                alt={displayName}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <Leaf className="w-5 h-5 text-emerald-400" />
            )}
          </div>

          {/* Plant info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
            {plant.scientific_name && (
              <p className="text-[11px] text-gray-500 truncate italic">{plant.scientific_name}</p>
            )}
          </div>

          {/* Care phase badge */}
          {isActive && (
            <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium text-emerald-600 bg-emerald-50 rounded-full">
              Jetzt pflegen
            </span>
          )}
        </button>
      </li>
    );
  };

  const renderSectionHeader = (label) => (
    <li
      key={label}
      className="sticky top-0 z-10 px-4 py-2 bg-gray-50 border-b border-gray-100"
    >
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
    </li>
  );

  return (
    <div
      className="flex flex-col h-full bg-white overflow-hidden"
      onTouchStart={isMobile ? onTouchStart : undefined}
      onTouchMove={isMobile ? onTouchMove : undefined}
      onTouchEnd={isMobile ? onTouchEnd : undefined}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white flex-shrink-0">
        <div>
          <h1 className="text-base font-semibold text-gray-900">Pflanzenbestand</h1>
          {!isEmpty && (
            <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
              {resolvedPlants.length} {resolvedPlants.length === 1 ? 'Pflanze' : 'Pflanzen'}
              {!isOnline && (
                <span
                  className="inline-block w-2 h-2 bg-gray-400 rounded-full"
                  aria-label="Offline"
                />
              )}
            </p>
          )}
        </div>
        {/* "Ansicht öffnen" — mobile only, per plan spec */}
        {isMobile && (
          <button
            onClick={() => openEditor()}
            className="flex items-center gap-1.5 px-3 h-9 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            aria-label="Gartenansicht öffnen"
          >
            ▤ Ansicht öffnen
          </button>
        )}
      </div>

      {/* Search bar — only when plants exist */}
      {!isEmpty && (
        <div className="px-4 py-2 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Pflanze suchen"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-colors"
              aria-label="Pflanzen filtern"
            />
          </div>
        </div>
      )}

      {/* Scrollable list area */}
      <div className="flex-1 overflow-y-auto">
        {/* Offline banner */}
        {!isOnline && (
          <div className="bg-gray-100 text-gray-900 text-xs px-4 py-3" role="status">
            Keine Verbindung. Die Daten werden aktualisiert, sobald du wieder online bist.
          </div>
        )}

        {/* Pull-to-refresh indicator */}
        {isMobile && (refreshing || pullDelta > 20) && (
          <div className="flex justify-center py-2">
            <div
              className={`w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full ${refreshing ? 'animate-spin' : ''}`}
            />
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-500">Pflanzen werden geladen…</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && isEmpty && (
          <div className="flex flex-col items-center justify-center py-16 px-6 gap-4 text-center">
            <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center">
              <Leaf className="w-8 h-8 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Noch keine Pflanzen im Garten.</p>
              <p className="text-xs text-gray-500 mt-1">Füge Pflanzen im Garten-Editor hinzu.</p>
            </div>
            <button
              onClick={() => openEditor()}
              className="flex items-center gap-1.5 px-4 h-10 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
            >
              <Plus className="w-4 h-4" /> Pflanze hinzufügen
            </button>
          </div>
        )}

        {/* Search no results */}
        {!isLoading && !isEmpty && filteredPlants.length === 0 && (
          <div className="text-center py-12 text-sm text-gray-500">
            Keine Pflanzen gefunden.
          </div>
        )}

        {/* Plant list — default (< 50) */}
        {!isLoading && filteredPlants.length > 0 && !useGroupedLayout && (
          <ul>{filteredPlants.map(renderPlantRow)}</ul>
        )}

        {/* Plant list — grouped (50+) */}
        {!isLoading && filteredPlants.length > 0 && useGroupedLayout && (
          <ul>
            {activeGroup.length > 0 && (
              <>
                {renderSectionHeader('Aktuelle Pflegephase')}
                {activeGroup.map(renderPlantRow)}
              </>
            )}
            {otherGroup.length > 0 && (
              <>
                {renderSectionHeader('Keine Pflege aktuell')}
                {otherGroup.map(renderPlantRow)}
              </>
            )}
          </ul>
        )}
      </div>

      {/* Plant Detail Sheet */}
      <PlantDetailSheet
        isOpen={!!selectedPlant}
        onClose={() => setSelectedPlant(null)}
        plant={selectedPlant}
      />
    </div>
  );
};

export default PlantListView;
